import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'What is an organisational ombuds?',
    answer: 'An organisational ombuds is an independent, impartial and confidential resource that helps people explore workplace concerns, conflict and options for resolution outside formal management or HR pathways. We provide a safe, neutral space for issues to surface early — before they escalate into formal complaints or legal matters.'
  }, {
    question: 'We already have an internal HR team. Why would we need external support?',
    answer: "Your HR team is essential — and they're also part of the organisation. Some issues need a pathway that sits outside the reporting structure entirely. An external ombuds gives employees and leaders a confidential space to raise concerns they wouldn't raise internally. We work alongside your HR team, not instead of them."
  }, {
    question: 'How is this different from our Employee Assistance Program (EAP)?',
    answer: 'EAPs provide personal psychological support for individuals. An external ombuds addresses workplace and systemic issues — conflict between people, cultural concerns, leadership challenges, and trust breakdowns. We focus on resolving the workplace issue itself, not just supporting the individual experiencing it. Both are valuable; they serve different purposes.'
  }, {
    question: 'Is EquipMe part of our internal HR team?',
    answer: 'No. EquipMe works as an external, independent support layer that complements your HR, legal, leadership and wellbeing functions. Our independence means employees and leaders can raise concerns confidentially, without fear of bias or organisational consequences.'
  }, {
    question: 'Can EquipMe support employees confidentially?',
    answer: 'Yes. EquipMe provides a confidential space for people to raise concerns, explore options and understand constructive next steps. We only report de-identified themes and systemic issues to leadership — individual conversations remain confidential unless there is an imminent risk of serious harm.'
  }, {
    question: 'Does EquipMe conduct formal investigations?',
    answer: 'EquipMe focuses on early intervention, mediation and conflict resolution — not formal investigations. If a formal investigation is needed, we can support workplace assessments and advisory work, but we will clearly scope this with you first.'
  }, {
    question: 'Who usually engages EquipMe?',
    answer: 'Senior HR leaders, executives, legal teams, CEOs and organisations seeking independent support with conflict, culture transformation and leadership complexity. We work with corporate, resources, government and community sectors across Australia.'
  }, {
    question: 'How quickly can EquipMe respond to a live issue?',
    answer: 'We typically respond to enquiries within 24 hours. For urgent matters, we can often arrange an initial consultation within 48-72 hours. Our goal is to help you address issues before they escalate further.'
  }, {
    question: 'What does a typical engagement look like?',
    answer: 'Engagements are tailored to your needs. Some organisations start with a single facilitated conversation. Others need ongoing systemic support over 3-6 months. After an initial confidential consultation, we provide a tailored proposal outlining scope, timeframes and investment.'
  }, {
    question: 'Is this suitable for small organisations or only large ones?',
    answer: "EquipMe works with organisations of all sizes. Whether you're a 20-person team or a 2,000-person enterprise, workplace conflict and culture issues require the same level of care, independence and expertise. We tailor our approach to your scale and budget."
  }];
  return <section className="py-24 bg-brand-off-white" data-id="element-77">
      <div className="max-w-4xl mx-auto px-6 md:px-12" data-id="element-78">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-79">
          <h2 className="text-4xl md:text-5xl font-serif text-brand-charcoal mb-4" data-id="element-80">
            Questions leaders often ask before engaging independent support.
          </h2>
        </motion.div>

        <div className="space-y-4" data-id="element-81">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.05
        }} className="bg-white rounded-sm border border-gray-200 overflow-hidden" data-id="element-82">
              <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className={`w-full px-8 py-6 flex items-center justify-between text-left transition-colors ${openIndex === index ? 'bg-brand-teal/5 border-l-4 border-brand-teal' : 'hover:bg-gray-50'}`} data-id="element-83">
                <span className="text-lg font-serif text-brand-charcoal pr-4" data-id="element-84">
                  {faq.question}
                </span>
                <ChevronDown size={20} className={`shrink-0 text-brand-teal transition-transform ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-85" />
              </button>

              <AnimatePresence data-id="element-86">
                {openIndex === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="overflow-hidden" data-id="element-87">
                    <div className="px-8 py-6 text-brand-charcoal/80 leading-relaxed border-t border-gray-100 font-normal text-[15px]" data-id="element-88">
                      {faq.answer}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mt-12 text-center" data-id="element-89">
          <p className="text-brand-charcoal/80 mb-6 font-medium text-lg" data-id="element-90">
            The best way to find out if we can help is a conversation.
          </p>
          <a href="#consultation" className="inline-flex items-center justify-center bg-brand-teal hover:bg-brand-deep-purple text-white px-8 py-4 rounded-sm font-semibold transition-colors shadow-lg shadow-brand-teal/20" data-id="element-91">
            Book a confidential consultation
          </a>
        </motion.div>
      </div>
    </section>;
}